<script lang="ts">
  import type { GrammarError, Correction, AIComment } from '$lib/types/grammar'
  import { aiSuggestionsStore } from '$lib/stores/aiSuggestions'
  import { errorFocusStore } from '$lib/stores/errorFocus'
  import { get } from 'svelte/store'
  import ErrorText from './error/ErrorText.svelte'
  import ErrorSuggestions from './error/ErrorSuggestions.svelte'
  import AICommentDisplay from './error/AIComment.svelte'
  import ErrorActions from './error/ErrorActions.svelte'
  
  interface Props {
    error: GrammarError
    onApplyCorrection: (correctionId: string) => void
    onApplyAlternative?: (alternative: string) => void
    onIgnore: () => void
    onKeepOriginal?: () => void
    onMouseEnter?: () => void
    onMouseLeave?: () => void
  }

  let { error, onApplyCorrection, onApplyAlternative, onIgnore, onKeepOriginal, onMouseEnter, onMouseLeave }: Props = $props()
  
  let isHovered = $state(false)
  let aiComment = $state<AIComment | null>(null)
  let isLoadingAI = $state(false)
  let isKeptOriginal = $state(false)
  let focusState = $state(get(errorFocusStore))
  
  // Subscribe to focus store
  $effect(() => {
    const unsubscribe = errorFocusStore.subscribe(state => {
      focusState = state
    })
    
    return unsubscribe
  })
  
  // Load AI comment if available  
  $effect(() => {
    // Always prefer aiComment directly attached to error
    if (error.aiComment) {
      aiComment = error.aiComment
    } else {
      // Fall back to store
      const storeState = $aiSuggestionsStore
      aiComment = storeState.comments.get(error.id) || null
      isLoadingAI = storeState.analyzingErrors.has(error.id)
    }
  })
  
  function applyCorrection(correction: Correction) {
    onApplyCorrection(correction.id)
  }
  
  function handleIgnore(e: MouseEvent) {
    e.stopPropagation()
    onIgnore()
  }
  
  function handleKeepOriginal(e: MouseEvent) {
    e.stopPropagation()
    onKeepOriginal?.()
  }
  
  // Get help text from correction or error
  // Show grammar check results even for merged errors
  const helpText = $derived(
    error.help || error.corrections?.[0]?.description || error.message || ''
  )
  
  // Initialize isKeptOriginal from error state
  $effect(() => {
    isKeptOriginal = error.isOriginalKept || error.userAction === 'kept-original' || false
  })
  
  // Error class based on state
  const errorClass = $derived(
    isKeptOriginal ? 'warning' : 'error'
  )
  
  function handleOriginalClick(e: MouseEvent) {
    e.stopPropagation()
    // Don't toggle locally - let parent handle state
    // isKeptOriginal = !isKeptOriginal
    // Always call onKeepOriginal to toggle the state in parent
    onKeepOriginal?.()
  }
</script>

<div 
  class="error-card"
  class:hovered={isHovered}
  class:focused={focusState.focusedErrorId === error.id}
  data-error-id={error.id}
  role="article"
  onmouseenter={() => {
    isHovered = true
    errorFocusStore.focusFromCard(error.id)
    onMouseEnter?.()
  }}
  onmouseleave={() => {
    isHovered = false
    errorFocusStore.clearFocus()
    onMouseLeave?.()
  }}
>
  <!-- Ignore button (X) - appears on hover -->
  <ErrorActions {isHovered} onIgnore={handleIgnore} />
  
  <div class="error-line">
    <!-- Original text -->
    <ErrorText 
      originalText={error.originalText || error.original || ''}
      {errorClass}
      {isKeptOriginal}
      onClick={handleOriginalClick}
    />
    
    <!-- Arrow -->
    <span class="arrow">→</span>
    
    <!-- Inline suggestions -->
    <ErrorSuggestions
      corrections={error.corrections}
      suggestions={error.suggestions}
      onApplyCorrection={applyCorrection}
    />
  </div>
  
  <!-- Grammar check result with icon -->
  {#if helpText && (!error.isAIOnly || error.hasBothChecks)}
    <div class="help-text">
      <span class="grammar-icon-minimal">✓</span>
      <span>{helpText}</span>
    </div>
  {/if}
  
  <!-- AI Comment Component -->
  <AICommentDisplay
    {aiComment}
    {isLoadingAI}
    isAIOnly={error.isAIOnly}
    {helpText}
    {isKeptOriginal}
    {onApplyAlternative}
    onKeepOriginal={handleKeepOriginal}
  />
</div>

<style>
  /* Ultra-minimal error card */
  .error-card {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 6px 0 6px 3px;
    margin-bottom: 8px;
    margin-left: -3px;
    font-size: 14px;
    position: relative;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    border-left: 3px solid transparent;
    transform: translateX(0);
    will-change: transform, background, border-color;
    contain: layout style;
  }
  
  .error-card:hover,
  .error-card.hovered {
    background: rgba(0, 0, 0, 0.04);
    border-left-color: rgba(0, 0, 0, 0.15);
    transform: translateX(2px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
  
  .error-card.focused {
    background: rgba(79, 70, 229, 0.05);
    border-left-color: rgba(79, 70, 229, 0.3);
    animation: focusPulse 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes focusPulse {
    0% {
      transform: translateX(0);
    }
    50% {
      transform: translateX(4px);
    }
    100% {
      transform: translateX(2px);
    }
  }
  
  /* Single line layout */
  .error-line {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    line-height: 1.4;
  }
  
  /* Arrow */
  .arrow {
    color: var(--text-secondary);
    font-size: 12px;
    flex-shrink: 0;
  }
  
  /* Help text */
  .help-text {
    display: flex;
    align-items: flex-start;
    font-size: 11px;
    color: var(--text-secondary);
    line-height: 1.4;
    margin-top: 4px;
    margin-left: 2px;
  }
  
  /* Icon styles */
  .grammar-icon-minimal {
    font-size: 12px;
    width: 16px;
    flex-shrink: 0;
    color: var(--success);
    display: inline-block;
    text-align: center;
    margin-right: 4px;
  }
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    .error-card {
      padding: 8px 0;
      margin-bottom: 10px;
    }
    
    .help-text {
      font-size: 12px;
    }
  }
  
  /* Mobile dark mode padding adjustment */
  @media (max-width: 768px) and (prefers-color-scheme: dark) {
    :global(:root:not(.light-theme)) .error-card {
      padding: 10px 10px;
    }
  }
  
  @media (max-width: 768px) {
    :global(.dark-theme) .error-card {
      padding: 10px 10px;
    }
  }
  
  /* Dark mode high contrast */
  :global(.dark-theme) .error-card {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 10px;
  }
  
  :global(.dark-theme) .error-card:hover,
  :global(.dark-theme) .error-card.hovered {
    background: rgba(255, 255, 255, 0.1);
    border-left-color: rgba(255, 255, 255, 0.2);
    transform: translateX(2px);
    box-shadow: 0 2px 6px rgba(255, 255, 255, 0.05);
  }
  
  :global(.dark-theme) .error-card.focused {
    background: rgba(147, 145, 255, 0.1);
    border-left-color: rgba(147, 145, 255, 0.3);
  }
  
  :global(.dark-theme) .help-text {
    margin-left: 0;
  }
  
  /* Auto dark mode (system preference) */
  @media (prefers-color-scheme: dark) {
    :global(:root:not(.light-theme)) .error-card {
      background: rgba(255, 255, 255, 0.03);
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 10px;
    }
    
    :global(:root:not(.light-theme)) .error-card:hover,
    :global(:root:not(.light-theme)) .error-card.hovered {
      background: rgba(255, 255, 255, 0.1);
      border-left-color: rgba(255, 255, 255, 0.2);
      transform: translateX(2px);
      box-shadow: 0 2px 6px rgba(255, 255, 255, 0.05);
    }
    
    :global(:root:not(.light-theme)) .error-card.focused {
      background: rgba(147, 145, 255, 0.1);
      border-left-color: rgba(147, 145, 255, 0.3);
    }
    
    :global(:root:not(.light-theme)) .help-text {
      margin-left: 0;
    }
  }
  
  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    .error-card {
      transition: none;
    }
  }
</style>